/**
 * The complete list of events triggered and handled by {@link PlanetMediaPlayer}
 * @enum {PlanetMediaPlayerEvents}
 */
export enum PlanetMediaPlayerEvents {

    BeforeXhrRequest = 'beforeXhrRequest',

    PlaybackSessionCreate = 'playbackSessionCreate',
    PlaybackSessionContentLoad = 'playbackSessionContentLoad',
    PlaybackSessionStart = 'playbackSessionStart',
    PlaybackSessionResume = 'playbackSessionResume',
    PlaybackSessionSeek = 'playbackSessionSeek',
    PlaybackSessionPause = 'playbackSessionPause',
    PlaybackSessionEnd = 'playbackSessionEnd',

    Autostart = 'autostart',
    AutostartFailed = 'autostartFailed',

    HlsManifestLoadRetry = 'hlsManifestLoadRetry',
    TextTrackAdded = 'textTrackAdded',

    /**
     * Fired when a remote text track was added and cues where initialized
     */
    TextTrackLoaded = 'textTrackLoaded',

    TextTrackCueChange = 'textTrackCueChange',

    CueChange = 'cuechange',

    MetadataCueChange = 'metadataCueChange',

    /**
     * Catch all events
     */
    PlayerEvent = 'player:event',
    KeyDown = 'keyDown',
    KeyUp = 'keyUp',
    /**
     * Triggered when the user clicks on the player element
     */
    UserInteraction = 'userInteraction',
    /**
     * Triggered when the player setup completed successfully.
     * To be considered successful a player must have a valid configuration
     */
    Ready = 'ready',
    /**
     * Triggered before the underlying player setup begins
     */
    BeforeSetup = 'beforeSetup',
    /**
     * Triggered when the underlying player emits the 'ready' event. This is different
     * from {@link PlanetMediaPlayerEvents.PlayerEvent}. This is a good time to hook in and interact
     * with the underlying implementation before {@link PlanetMediaPlayer} interacts with it. At this
     * point the underlying player's API will be ready to use but no event handlers will be setup.
     */
    Setup = 'setup',
    /**
     * Triggered when the underlying player's implementation fails due to missconfiguration or invalid
     * {@link MediaItem[]} playlist is provided.
     */
    SetupError = 'setupError',
    /**
     * Triggered immediately before disposing the player. At this point all the player components and plugins still
     * exists and the player was not removed from the DOM nor stopped yet.
     */
    BeforeRemove = 'beforeRemove',
    /*
     * Triggered immediately after the underlying player was disposed.
     */
    Remove = 'remove',
    /**
     * Triggered if the player becomes visible or invisible in the current document's Viewport
     */
    VisibilityChange = 'viewable',
    /**
     * Triggered when the player becomes visible in the current document's viewport
     */
    Visible = 'visible',
    /**
     * Triggered when the player becomes invisible in the current document's viewport
     */
    Hidden = 'hidden',
    /**
     * Triggered when the {@link PlaylistController} successfully parses the current playlist
     */
    Playlist = 'playlist',
    /**
     * Triggered after the player loads a new {@link MediaItem} successfully. This happens during
     * the initial setup and when the current playlist changes.
     */
    PlaylistItem = 'playlistItem',
    /**
     * Triggered when the last playlist item in the playlist completes
     */
    PlaylistComplete = 'playlistComplete',
    /**
     * Triggered every time the player changes it's {@link MediaControllerState}
     */
    StateChange = 'stateChange',
    /**
     * Triggered when the percentage of loaded media increases
     */
    BufferChange = 'bufferChange',
    Cast = 'cast',
    /**
     * Triggered when the player is setup to stat automatically but doesnt due to browser restrictions
     */
    AutostartNotAllowed = 'autostartNotAllowed',
    /**
     * Triggered when the player can start playing the loaded source
     */
    CanPlay = 'canplay',
    /**
     * Triggered when the play method is invoked on a specific playlist item
     */
    FirstPlay = 'firstPlay',
    /**
     * Triggered when the state of the player switches from paused to playing
     */
    Play = 'play',
    /**
     * Triggered every time the player is effectively playing the loaded source.
     */
    Playing = 'playing',
    /**
     * Triggered when the player is paused
     */
    Pause = 'pause',
    /**
     */
    PlayAttemptFailed = 'playAttemptFailed',
    /**
     * Triggered when the player starts buffering content
     */
    Buffer = 'buffer',
    /**
     * Triggered when there's no media currently loaded in the player
     */
    Idle = 'idle',
    /**
     * Triggered when the player completes playback of the current {@link MediaItem}
     */
    Complete = 'complete',
    /**
     * Triggered when the playback stats for the first time for the current {@link MediaItem}
     */
    FirstFrame = 'firstFrame',
    /**
     * Triggered when there's an error during playback. The difference between {@link PlanetMediaPlayerEvents.Error}
     * and {@link PlanetMediaPlayerEvents.SetupError} is that when {@link PlanetMediaPlayerEvents.Error} is triggered
     * the player already completed setup successfully and the currently loaded source is supported by the player.
     */
    Error = 'error',
    /**
     *
     */
    Warning = 'warning',
    /**
     * Triggered when the playback rate changes
     */
    PlaybackRateChanged = 'playbackRateChanged',
    /**
     * Triggered when the player starts seeking
     */
    Seek = 'seek',
    /**
     * Triggered many times during seeks (every time the "target" timestamp changes)
     */
    Seeking = 'seeking',
    /**
     * Triggered when the player completed the seek operation and the currentPosition was updated
     */
    Seeked = 'seeked',
    /**
     * Triggered when the current position changes
     */
    Time = 'time',
    /**
     * Triggered when the player is muted or unmuted
     */
    Mute = 'mute',
    /**
     * Triggered when the volume changes
     */
    Volume = 'volume',
    /**
     * Triggered when the player enters or leaves fullscreen
     */
    Fullscreen = 'fullscreen',
    /**
     * Triggered when the player is resized
     */
    Resize = 'resize',
    /**
     * Triggered when the HLS handler parsed the current master playlist and got information about
     * the renditions available for the current source
     */
    Levels = 'levels',
    /**
     * Triggered when the number of renditions changes
     */
    LevelsChanged = 'levelsChanged',
    /**
     * Triggered when the player changes the current rendition
     */
    VisualQuality = 'visualQuality',
    AudioTracks = 'audioTracks',
    AudioTrackChanged = 'audioTrackChanged',
    CaptionsList = 'captionsList',
    CaptionsChanged = 'captionsChanged',
    Controls = 'controls',
    DisplayClick = 'displayClick',
    DisplayDoubleClick = 'displayDoubleClick',
    BeforePlay = 'beforePlay',
    BeforeComplete = 'beforeComplete',

    Meta = 'meta',
    MetadataCueParsed = 'metadataCueParsed',
    CinematicViewToggle = 'cinematicView',
}

export const PlayerEvents = Object.values(PlanetMediaPlayerEvents);
